import { SquareChevronRight } from "lucide-react";

import { cn } from "@acme/ui";
import { Button } from "@acme/ui/button";
import { ScrollArea } from "@acme/ui/scroll-area";

import type { WorkerLog } from "../_hooks/_utils/codemod-sandbox-worker";

interface DebugPanelProps {
  logs: WorkerLog[];
  onClearLogs: () => void;
}

export function DebugPanel({ logs, onClearLogs }: DebugPanelProps) {
  const hasErrors = logs.some((log) => log.type === "error");

  return (
    <div className="flex h-full flex-col">
      <div className="border-b-border bg-background flex w-full flex-row items-center justify-between gap-4 border-b px-2 py-2 shadow dark:shadow-xl">
        <h3 className="flex items-center gap-1 font-medium">
          <SquareChevronRight className="size-5" />
          Console
          {hasErrors && (
            <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">
              Errors
            </span>
          )}
        </h3>
        <Button onClick={onClearLogs} variant="outline" size="xs">
          Clear
        </Button>
      </div>
      <div className="bg-muted/50 flex-1 font-mono text-sm">
        <ScrollArea className="h-full">
          {logs.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No logs to display. Run your codemod to see debug information.
            </div>
          ) : (
            <div className="space-y-1 font-mono text-xs">
              {logs.map((log, index) => (
                <div
                  key={index}
                  className={cn(
                    "border-b px-3 py-2",
                    getLogTypeClass(log.type),
                  )}
                >
                  <span className="text-gray-500">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>{" "}
                  {log.args.join(" ")}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}

// Helper function to get the CSS class for a log type
function getLogTypeClass(type: WorkerLog["type"]): string {
  switch (type) {
    case "error":
      return "text-red-500";
    case "warn":
      return "text-yellow-500";
    case "info":
      return "text-blue-500";
    case "debug":
      return "text-purple-500";
    default:
      return "text-gray-700 dark:text-gray-300";
  }
}
